import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Tag, TagTranslation } from 'src/app/interfaces/tag';
import { ApiService } from 'src/app/services/api.service';
import { InitService } from 'src/app/services/init.service';
import { TagsService } from 'src/app/services/tags.service';
import { ToastService } from 'src/app/services/toast.service';

type SupportedLang = 'en' | 'nl';

interface TranslationForm {
  tag_translation_id?: number;
  name: string;
  description: string;
}

interface ParentTagOption {
  tag_id: number;
  name: string;
}

interface ContextRow {
  tag_context_id?: number;
  level: number | null;
  phase: number | null;
}

@Component({
  selector: 'app-edit-tag',
  templateUrl: './edit-tag.component.html',
  styleUrls: ['./edit-tag.component.scss'],
})
export class EditTagComponent implements OnInit, OnDestroy {
  @Input() selfModal: NgbModalRef | null = null;
  @Input() tag: Tag | null = null;
  @Output() refresh = new EventEmitter<void>();

  public languages: SupportedLang[] = ['en', 'nl'];
  public activeLang: SupportedLang = 'en';

  public verified: boolean = false;
  public translations: Record<SupportedLang, TranslationForm> = {
    en: { name: '', description: '' },
    nl: { name: '', description: '' },
  };

  private originalTranslationIds: Partial<Record<SupportedLang, number>> = {};

  public saving: boolean = false;

  // Parent tag selector state
  public parentTag: ParentTagOption | null = null;
  public parentSearch: string = '';
  public parentResults: ParentTagOption[] = [];
  public parentSearching: number = 0;
  public parentShowResults: boolean = false;
  private parentSearchChanged: Subject<string> = new Subject<string>();
  private parentSearchSub: Subscription | null = null;

  // DH contexts editor state
  public dhOrganization: boolean = false;
  public phases: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
  public levels: number[] = [1, 2, 3, 4, 5, 6, 7, 8];
  public contexts: ContextRow[] = [];
  private originalContexts: ContextRow[] = [];

  get isEdit(): boolean {
    return !!this.tag?.tag_id;
  }

  constructor(
    private tagsService: TagsService,
    private toastr: ToastService,
    private translate: TranslateService,
    private api: ApiService,
    private initService: InitService
  ) {}

  ngOnInit(): void {
    this.dhOrganization = !!this.initService.getCurrentOrg()?.dh_organization;

    if (this.tag?.tag_id) {
      this.verified = !!this.tag.verified;
      (this.tag.translations || []).forEach((t: TagTranslation) => {
        if (t.language === 'en' || t.language === 'nl') {
          this.translations[t.language] = {
            tag_translation_id: t.tag_translation_id,
            name: t.name || '',
            description: t.description || '',
          };
          if (t.tag_translation_id) {
            this.originalTranslationIds[t.language] = t.tag_translation_id;
          }
        }
      });

      if (this.tag.parent_tag && this.tag.parent_tag.tag_id) {
        this.parentTag = {
          tag_id: this.tag.parent_tag.tag_id,
          name: this.tag.parent_tag.info?.name || `#${this.tag.parent_tag.tag_id}`,
        };
      }

      (this.tag.context || []).forEach((c) => {
        const row: ContextRow = {
          tag_context_id: c.tag_context_id,
          level: c.level?.level ?? null,
          phase: c.phase?.phase ?? null,
        };
        this.contexts.push(row);
        this.originalContexts.push({ ...row });
      });
    }

    this.parentSearchSub = this.parentSearchChanged
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((q) => this.runParentSearch(q));
  }

  ngOnDestroy(): void {
    this.parentSearchSub?.unsubscribe();
  }

  public setActiveLang(lang: SupportedLang): void {
    this.activeLang = lang;
  }

  public closeModal(): void {
    this.selfModal?.close();
  }

  public onParentSearchChange(text: string): void {
    this.parentSearch = text;
    if (!text.trim().length) {
      this.parentResults = [];
      this.parentShowResults = false;
      return;
    }
    this.parentSearchChanged.next(text);
  }

  private runParentSearch(q: string): void {
    this.parentSearching += 1;
    this.parentShowResults = true;
    this.api
      .get('/search/tags?q=' + encodeURIComponent(q))
      .then((res: any) => {
        this.parentSearching -= 1;
        const rows = (res?.data?.tags || []) as any[];
        this.parentResults = rows
          .filter((r) => r?.tag?.tag_id && r.tag.tag_id !== this.tag?.tag_id)
          .map((r) => ({ tag_id: r.tag.tag_id, name: r.name }));
      })
      .catch(() => {
        this.parentSearching -= 1;
        this.parentResults = [];
      });
  }

  public selectParent(option: ParentTagOption): void {
    this.parentTag = option;
    this.parentSearch = '';
    this.parentResults = [];
    this.parentShowResults = false;
  }

  public clearParent(): void {
    this.parentTag = null;
  }

  public addContext(): void {
    this.contexts.push({ level: null, phase: null });
  }

  public removeContext(index: number): void {
    this.contexts.splice(index, 1);
  }

  private buildContextsPayload(): { contexts: any[]; deleteContexts: any[] } {
    const validRows = this.contexts.filter((c) => c.level !== null && c.phase !== null);

    const contexts = validRows.map((c) => ({
      tag_context_id: c.tag_context_id,
      level: c.level,
      phase: c.phase,
    }));

    const keptIds = new Set(
      validRows.filter((c) => c.tag_context_id).map((c) => c.tag_context_id!)
    );
    const deleteContexts = this.originalContexts
      .filter((c) => c.tag_context_id && !keptIds.has(c.tag_context_id))
      .map((c) => ({ tag_context_id: c.tag_context_id }));

    return { contexts, deleteContexts };
  }

  public save(): void {
    const hasAnyName = this.languages.some((l) => this.translations[l].name.trim().length > 0);
    if (!hasAnyName) {
      this.toastr.showError(this.translate.instant('admin:tags:modal:error:name_required'));
      return;
    }

    this.saving = true;

    if (this.isEdit) {
      this.updateExisting();
    } else {
      this.createNew();
    }
  }

  private createNew(): void {
    const info: any = {};
    this.languages.forEach((lang) => {
      const t = this.translations[lang];
      if (t.name.trim().length) {
        info[lang] = { name: t.name.trim(), description: t.description.trim() || undefined };
      }
    });

    const payload: any = { verified: this.verified, info };
    if (this.parentTag) {
      payload.parent_tag_id = this.parentTag.tag_id;
    }
    if (this.dhOrganization) {
      const { contexts } = this.buildContextsPayload();
      if (contexts.length) payload.contexts = contexts;
    }

    this.tagsService
      .create(payload)
      .then((resp: any) => {
        this.saving = false;
        if (resp?.result === 'success' || resp?.data?.tag) {
          this.toastr.showSuccess(this.translate.instant('admin:tags:modal:created'));
          this.refresh.emit();
          this.closeModal();
        } else {
          this.toastr.showError(this.translate.instant('admin:tags:modal:error:save'));
        }
      })
      .catch(() => {
        this.saving = false;
        this.toastr.showError(this.translate.instant('admin:tags:modal:error:save'));
      });
  }

  private updateExisting(): void {
    if (!this.tag) return;

    const info: any = {};
    const deleteInfo: any[] = [];

    this.languages.forEach((lang) => {
      const t = this.translations[lang];
      const originalId = this.originalTranslationIds[lang];
      const hasName = t.name.trim().length > 0;

      if (hasName) {
        info[lang] = {
          tag_translation_id: t.tag_translation_id,
          name: t.name.trim(),
          description: t.description.trim(),
        };
      } else if (originalId) {
        deleteInfo.push({ tag_translation_id: originalId });
      }
    });

    const payload: any = {
      verified: this.verified,
      parent_tag_id: this.parentTag ? this.parentTag.tag_id : null,
      info,
    };

    let deleteContexts: any[] = [];
    if (this.dhOrganization) {
      const built = this.buildContextsPayload();
      payload.contexts = built.contexts;
      deleteContexts = built.deleteContexts;
    }

    if (deleteInfo.length || deleteContexts.length) {
      payload.delete = {};
      if (deleteInfo.length) payload.delete.info = deleteInfo;
      if (deleteContexts.length) payload.delete.context = deleteContexts;
    }

    this.tagsService
      .update(this.tag.tag_id, payload)
      .then((resp: any) => {
        this.saving = false;
        if (resp?.result === 'success') {
          this.toastr.showSuccess(this.translate.instant('admin:tags:modal:updated'));
          this.refresh.emit();
          this.closeModal();
        } else {
          this.toastr.showError(this.translate.instant('admin:tags:modal:error:save'));
        }
      })
      .catch(() => {
        this.saving = false;
        this.toastr.showError(this.translate.instant('admin:tags:modal:error:save'));
      });
  }
}
